import clsx from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import Cookies from 'universal-cookie';

import Tooltip from '@components/Tooltip';
import { useOrgCharts } from '@hooks';
import {
  cloud_database_path,
  corporate_landing_path,
  create_company_index_path,
  enterprise_powerlist_2023_path,
  events_path,
  forum_path,
  generative_ai_database_path,
  gigafactory_database_path,
  highlight_path,
  kids_tech_survey_path,
  newsletter_list_path,
  opinion_feed_path,
  org_charts_path,
  partners_list_path,
  root_path,
  subscriber_directories_path,
  tech_ipo_tracker_path,
  ti50_database_path,
  ti_50_list_path,
  vc_diversity_index_path,
  video_library_path,
  weekend_path,
} from '@routes';
import { NavCurrentUser } from '@utils/backend-api/currentUser';

import NavDropdown from '../NavDropdown';
import sideNavStyles from '../SideNav/styles.module.scss';
import styles from './styles.module.scss';

const MAIN_PRO_LINKS = [
  { title: 'Org Charts', url: org_charts_path() },
  { title: 'Pro Weekly Newsletter', url: highlight_path('pro-weekly') },
  { title: 'Generative AI Database', url: generative_ai_database_path() },
  { title: 'The Information 50 Database', url: ti50_database_path() },
  { title: 'Cloud Database', url: cloud_database_path() },
  { title: 'Creator Economy Database', url: create_company_index_path() },
  { title: 'The Information 50', url: ti_50_list_path() },
];

const OTHER_PRO_LINKS = [
  { title: 'Enterprise Tech Power List', url: enterprise_powerlist_2023_path() },
  { title: 'Gigafactory Database', url: gigafactory_database_path() },
  { title: 'VC Diversity Index', url: vc_diversity_index_path() },
  { title: 'Tech IPO Tracker', url: tech_ipo_tracker_path() },
  { title: 'Kids and Technology Survey', url: kids_tech_survey_path() },
];

const EVENTS = [
  { title: 'Upcoming Events', url: events_path() },
  { title: 'Video Library', url: video_library_path() },
];

type variant = 'top' | 'side';

const cookies = new Cookies();

type tooltipShown = 'pro' | null;

const TOOLTIP_LOAD_DELAY = 2000;

export default function NavLinks({
  currentUser,
  isWebview,
  dropdownShown,
  setDropdownShown,
  variant,
  newForumPostsCount,
}: {
  currentUser: NavCurrentUser;
  isWebview?: boolean;
  dropdownShown: string;
  setDropdownShown: React.Dispatch<React.SetStateAction<string>>;
  variant: variant;
  newForumPostsCount: number;
}) {
  const { orgCharts } = useOrgCharts();

  const inSideNav = variant === 'side';
  const inTopNav = variant === 'top';

  const [tooltipShown, setTooltipShown] = useState<tooltipShown>(null);

  const allowTooltip = useMemo(
    () =>
      window.location.pathname === root_path() &&
      inTopNav &&
      !isWebview &&
      currentUser?.isProSubscriber,
    [currentUser, inTopNav, isWebview]
  );

  useEffect(() => {
    if (allowTooltip) {
      const hasVisitedHomePage = cookies.get('hasVisitedHomePagePro');
      cookies.set(
        'hasVisitedHomePagePro',
        (hasVisitedHomePage ? parseInt(hasVisitedHomePage) || 0 : 0) + 1
      );
    }
  }, [allowTooltip]);

  useEffect(() => {
    setTimeout(() => setTooltipShown('pro'), TOOLTIP_LOAD_DELAY);
  }, []);

  const showTooltip =
    (!cookies.get('hasVisitedHomePagePro') || cookies.get('hasVisitedHomePagePro') == 1) &&
    allowTooltip;

  return (
    <>
      <div
        className={clsx(styles.firstSection, {
          [styles.firstSideNavSection]: inSideNav,
          [sideNavStyles.sideNavSection]: inSideNav,
        })}
      >
        <div className={clsx({ [styles.linkAndTooltipWrapper]: inTopNav })}>
          <NavDropdown
            title="Pro"
            mainOptions={MAIN_PRO_LINKS}
            otherOptions={OTHER_PRO_LINKS}
            inSideNav={inSideNav}
            dropdownShown={dropdownShown}
            optionsDividerText="More Pro"
            setDropdownShown={setDropdownShown}
            highlighted
          />
          {showTooltip && (
            <Tooltip
              message="You have been granted Pro access to The Information."
              parentSeparation="50px"
              onClose={() => setTooltipShown(null)}
              position="bottomRight"
              showTooltip={tooltipShown === 'pro'}
              title="Welcome!"
              tooltipId="pro-tooltip"
            />
          )}
        </div>
        {inTopNav ? (
          <a className={clsx(styles.navLink, styles.highlightedNavLink)} href={org_charts_path()}>
            Org Charts
          </a>
        ) : (
          <NavDropdown
            title="Org Charts"
            mainOptions={orgCharts}
            inSideNav={inSideNav}
            dropdownShown={dropdownShown}
            setDropdownShown={setDropdownShown}
            highlighted
          />
        )}
        <a className={styles.navLink} href={newsletter_list_path()}>
          Newsletters
        </a>
        <a className={styles.navLink} href={weekend_path()}>
          Weekend
        </a>
        <a className={styles.navLink} href={opinion_feed_path()}>
          Opinion
        </a>
        <a className={styles.navLink} href={highlight_path('the-electric')}>
          The Electric
        </a>
        <NavDropdown
          title="Events"
          mainOptions={EVENTS}
          inSideNav={inSideNav}
          dropdownShown={dropdownShown}
          setDropdownShown={setDropdownShown}
        />
        <a className={styles.navLink} href={partners_list_path()}>
          Partners
        </a>
        <a className={styles.navLink} href={corporate_landing_path()}>
          Corporate
        </a>
      </div>
      <div
        className={clsx({
          [styles.secondTopNavSection]: inTopNav,
          [styles.secondSideNavSection]: inSideNav,
          [sideNavStyles.sideNavSection]: inSideNav,
        })}
      >
        {/* If we want nav link with blue/red wrapper logo, use this wrapper */}
        {/* <div className={clsx(styles.linkWrapper, styles.blueLinkWrapper)}>
            <div className={clsx(styles.linkWrapperText, styles.blueLinkWrapperText)}>NEW!</div>
            <a className={clsx(styles.navLink, styles.wrappedLink)}>
              SOME LINK TEXT
            </a>
          </div> */}
        <div className={clsx(styles.navLink)}>
          <a href={subscriber_directories_path()}>Directory</a>
        </div>
        <div className={clsx(styles.navLink, styles.noMargin)}>
          <a href={forum_path()} data-test="nav-link-forum">
            Forum{newForumPostsCount > 0 && ` (${newForumPostsCount})`}
          </a>
        </div>
      </div>
    </>
  );
}
